/**
 * Miyaar Luxury Watches - Core Theme Script
 * Complete AJAX Cart API, Interactive Modals, Variant Switching, & Watch Hotspots
 */

(function () {
  'use strict';

  // --- UTILITIES ---
  const formatMoney = (cents, format) => {
    if (typeof cents === 'string') {
      cents = cents.replace('.', '');
    }
    const value = (cents / 100).toFixed(2);
    const moneyFormat = format || (window.MiyaarConfig && window.MiyaarConfig.moneyFormat) || '${{amount}}';
    return moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
      switch (key) {
        case 'amount':
          return value;
        case 'amount_no_decimals':
          return (cents / 100).toFixed(0);
        case 'amount_with_comma_separator':
          return value.replace(/\./, ',');
        default:
          return value;
      }
    });
  };

  const showToast = (message) => {
    const toast = document.getElementById('miyaar-toast');
    const toastText = document.getElementById('toast-message-text');
    if (!toast || !toastText) return;

    toastText.textContent = message;
    toast.classList.add('show');
    clearTimeout(window.__toastTimeout);
    window.__toastTimeout = setTimeout(() => {
      toast.classList.remove('show');
    }, 3500);
  };

  const getCartFallbackImage = (contextText = '') => {
    const fallbackImages = window.MiyaarConfig && window.MiyaarConfig.cartFallbackImages;
    if (!fallbackImages) return '';
    const text = String(contextText || '').toLowerCase();
    if (text.includes('roman')) return fallbackImages.roman;
    if (text.includes('silver')) return fallbackImages.silver;
    return fallbackImages.gold;
  };

  const resolveMediaUrl = (img, contextText = '') => {
    let src = '';
    if (typeof img === 'string' && img.length > 0 && img !== 'undefined') {
      src = img;
    } else if (img && typeof img.url === 'string' && img.url.length > 0) {
      src = img.url;
    } else if (img && typeof img.src === 'string' && img.src.length > 0) {
      src = img.src;
    }

    if (!src || src.includes('no-image') || src === 'undefined') {
      src = getCartFallbackImage(contextText);
    }

    if (src.startsWith('//')) {
      return 'https:' + src;
    }
    return src;
  };

  // --- HEADER & SCROLL BEHAVIOR ---
  const initHeader = () => {
    const header = document.getElementById('site-header');
    if (!header) return;

    const isTransparent = header.getAttribute('data-transparent') === 'true';

    const handleScroll = () => {
      const scrollY = window.scrollY || window.pageYOffset;
      if (scrollY > 40) {
        header.classList.add('site-header--scrolled');
      } else {
        if (isTransparent) {
          header.classList.remove('site-header--scrolled');
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
  };

  // --- MOBILE MENU DRAWER ---
  const initMobileMenu = () => {
    const drawer = document.getElementById('mobile-menu-drawer');
    if (!drawer) return;

    const menuButtons = document.querySelectorAll('[data-action="toggle-mobile-menu"]');
    const closeDrawer = () => {
      drawer.classList.remove('active');
      drawer.setAttribute('aria-hidden', 'true');
      menuButtons.forEach((btn) => btn.setAttribute('aria-expanded', 'false'));
      document.body.style.overflow = '';
    };

    menuButtons.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        drawer.classList.add('active');
        drawer.setAttribute('aria-hidden', 'false');
        btn.setAttribute('aria-expanded', 'true');
        document.body.style.overflow = 'hidden';
      });
    });

    document.querySelectorAll('[data-action="close-mobile-menu"]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        closeDrawer();
      });
    });

    drawer.querySelectorAll('.mobile-nav a').forEach((link) => {
      link.addEventListener('click', closeDrawer);
    });
  };

  // --- SEARCH MODAL & LIVE SEARCH ---
  const initSearchModal = () => {
    const modal = document.getElementById('search-modal');
    const input = document.getElementById('SearchInput');
    const resultsContainer = document.getElementById('search-results-live');
    if (!modal) return;

    document.querySelectorAll('[data-action="open-search"]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        modal.classList.add('active');
        document.body.style.overflow = 'hidden';
        setTimeout(() => input && input.focus(), 150);
      });
    });

    document.querySelectorAll('[data-action="close-search"]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        modal.classList.remove('active');
        document.body.style.overflow = '';
      });
    });

    document.querySelectorAll('[data-action="clear-search"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (input) {
          input.value = '';
          input.focus();
        }
        if (resultsContainer) resultsContainer.innerHTML = '';
      });
    });

    // Live search debounced query
    let debounceTimer;
    if (input && resultsContainer) {
      input.addEventListener('input', (e) => {
        const query = e.target.value.trim();
        clearTimeout(debounceTimer);

        if (query.length < 2) {
          resultsContainer.innerHTML = '';
          return;
        }

        debounceTimer = setTimeout(() => {
          fetch(`/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=4`)
            .then((res) => res.json())
            .then((data) => {
              const products = data.resources.results.products || [];
              if (products.length === 0) {
                resultsContainer.innerHTML = `<p style="padding: 16px 0; color: var(--color-text-secondary); font-size: 0.85rem;">No matching timepieces found for "${query}".</p>`;
                return;
              }

              let html = '<div class="live-search-grid" style="display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 16px; margin-top: 24px;">';
              products.forEach((p) => {
                html += `
                  <a href="${p.url}" class="live-search-item" style="display: flex; gap: 12px; background: var(--color-bg-secondary); padding: 10px; border-radius: 4px; border: 1px solid var(--color-border);">
                    <img src="${resolveMediaUrl(p.featured_image, p.title)}" alt="${p.title}" style="width: 50px; height: 50px; object-fit: cover; border-radius: 2px;" onerror="this.onerror=null;this.src='/assets/cartier-tank-minimalist-gold-brown.jpg';">
                    <div>
                      <h5 style="font-size: 0.85rem; margin-bottom: 4px; color: var(--color-text-primary);">${p.title}</h5>
                      <span style="font-size: 0.8rem; color: var(--color-accent-gold); font-weight: 600;">${p.price}</span>
                    </div>
                  </a>
                `;
              });
              html += '</div>';
              resultsContainer.innerHTML = html;
            })
            .catch(() => {});
        }, 300);
      });
    }
  };

  // --- AJAX CART DRAWER ENGINE ---
  const CartDrawer = {
    drawer: null,
    itemsContainer: null,
    footerContainer: null,
    countBadges: [],
    subtotalDisplays: [],
    shippingContainer: null,

    init() {
      this.drawer = document.getElementById('cart-drawer');
      if (!this.drawer) return;

      this.itemsContainer = document.getElementById('cart-drawer-items-container');
      this.footerContainer = document.getElementById('cart-drawer-footer');
      this.countBadges = [
        document.getElementById('header-cart-counter'),
        document.getElementById('cart-drawer-count')
      ];
      this.subtotalDisplays = [
        document.getElementById('cart-drawer-subtotal')
      ];

      this.bindEvents();
    },

    bindEvents() {
      // Open / Close triggers
      document.querySelectorAll('[data-action="open-cart-drawer"]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.open();
        });
      });

      document.querySelectorAll('[data-action="close-cart-drawer"]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          if (btn.tagName === 'A' && btn.getAttribute('href') && btn.getAttribute('href') !== '#') {
            return;
          }
          e.preventDefault();
          this.close();
        });
      });

      // Quick Add buttons
      document.addEventListener('click', (e) => {
        const quickAddBtn = e.target.closest('[data-action="quick-add"]');
        if (quickAddBtn) {
          e.preventDefault();
          const variantId = quickAddBtn.getAttribute('data-variant-id');
          if (variantId) {
            this.addItem(variantId, 1, {}, quickAddBtn);
          }
        }

        // Remove item from drawer
        const removeBtn = e.target.closest('[data-action="remove-item"]');
        if (removeBtn) {
          e.preventDefault();
          const key = removeBtn.getAttribute('data-key');
          this.updateItem(key, 0);
        }

        // Qty increment / decrement in drawer
        const qtyBtn = e.target.closest('[data-action="qty-change"]');
        if (qtyBtn) {
          e.preventDefault();
          const delta = parseInt(qtyBtn.getAttribute('data-delta'), 10);
          const input = qtyBtn.parentElement.querySelector('.qty-input');
          if (input) {
            const key = input.getAttribute('data-key');
            const currentQty = parseInt(input.value, 10);
            const newQty = currentQty + delta;
            if (newQty >= 0) {
              this.updateItem(key, newQty);
            }
          }
        }

      });
    },

    open() {
      if (this.drawer) {
        this.drawer.classList.add('active');
        document.body.style.overflow = 'hidden';
      }
    },

    close() {
      if (this.drawer) {
        this.drawer.classList.remove('active');
        document.body.style.overflow = '';
      }
    },

    addItem(variantId, quantity = 1, properties = {}, triggerBtn = null) {
      if (triggerBtn) {
        triggerBtn.classList.add('loading');
        triggerBtn.disabled = true;
      }

      fetch(window.MiyaarConfig.routes.cartAddUrl + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: variantId,
          quantity: quantity,
          properties: properties
        })
      })
        .then((res) => {
          if (!res.ok) throw new Error('Failed to add timepiece');
          return res.json();
        })
        .then((item) => {
          showToast(`Acquisition added: ${item.title}`);
          this.fetchAndRenderCart(true);
        })
        .catch((err) => {
          showToast(err.message || 'Error adding timepiece');
        })
        .finally(() => {
          if (triggerBtn) {
            triggerBtn.classList.remove('loading');
            triggerBtn.disabled = false;
          }
        });
    },

    updateItem(key, quantity) {
      fetch(window.MiyaarConfig.routes.cartChangeUrl + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity })
      })
        .then((res) => res.json())
        .then(() => {
          this.fetchAndRenderCart(false);
        })
        .catch(() => {
          showToast('Failed to update reservation tray');
        });
    },

    fetchAndRenderCart(openAfter = false) {
      fetch(window.MiyaarConfig.routes.cartUrl + '.js')
        .then((res) => res.json())
        .then((cart) => {
          this.renderCart(cart);
          if (openAfter) {
            this.open();
          }
        });
    },

    renderCart(cart) {
      // Update counters
      this.countBadges.forEach((b) => {
        if (b) b.textContent = b.id === 'header-cart-counter' ? cart.item_count : `(${cart.item_count})`;
      });

      // Update Subtotal
      this.subtotalDisplays.forEach((s) => {
        if (s) s.textContent = formatMoney(cart.total_price);
      });

      // Render Items or Empty State
      if (!this.itemsContainer) return;

      if (cart.item_count === 0) {
        this.itemsContainer.innerHTML = `
          <div class="cart-drawer__empty">
            <div class="empty-icon-wrap">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><circle cx="12" cy="12" r="7"></circle><polyline points="12 9 12 12 13.5 13.5"></polyline></svg>
            </div>
            <h4>Your timepiece tray is currently empty</h4>
            <p>Explore our flagship handcrafted timepieces crafted for connoisseurs.</p>
            <a href="/collections/all" class="btn btn--primary">Discover Timepieces</a>
          </div>
        `;
        if (this.footerContainer) {
          this.footerContainer.classList.add('cart-drawer__footer--hidden');
        }
      } else {
        if (this.footerContainer) {
          this.footerContainer.classList.remove('cart-drawer__footer--hidden');
        }

        let itemsHtml = '<div class="cart-drawer__items-list">';
        cart.items.forEach((item, index) => {
          let propertiesHtml = '';
          if (item.properties && Object.keys(item.properties).length > 0) {
            propertiesHtml = '<div class="cart-drawer-item__properties">';
            for (let prop in item.properties) {
              if (item.properties[prop]) {
                propertiesHtml += `<span class="cart-property"><strong>${prop}:</strong> ${item.properties[prop]}</span> `;
              }
            }
            propertiesHtml += '</div>';
          }

          const imageContext = `${item.title || item.product_title || ''} ${item.variant_title || ''}`;
          const itemImg = resolveMediaUrl(item.image || item.featured_image, imageContext);
          const fallbackImg = getCartFallbackImage(imageContext);

          itemsHtml += `
            <div class="cart-drawer-item" data-line-item-key="${item.key}" data-line-index="${index + 1}">
              <div class="cart-drawer-item__image">
                <a href="${item.url}">
                  <img src="${itemImg}" alt="${item.title || item.product_title || 'Timepiece'}" width="80" height="80" loading="lazy" onerror="this.onerror=null;this.src='${fallbackImg}';">
                </a>
              </div>
              <div class="cart-drawer-item__details">
                <div class="cart-drawer-item__header">
                  <a href="${item.url}" class="cart-drawer-item__title">${item.product_title}</a>
                  <button type="button" class="cart-drawer-item__remove" data-action="remove-item" data-key="${item.key}" aria-label="Remove item">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
                  </button>
                </div>
                ${item.variant_title ? `<div class="cart-drawer-item__variant">${item.variant_title}</div>` : ''}
                ${propertiesHtml}
                <div class="cart-drawer-item__footer">
                  <div class="cart-qty-selector">
                    <button type="button" class="qty-btn" data-action="qty-change" data-delta="-1">−</button>
                    <input type="number" class="qty-input" value="${item.quantity}" min="1" max="99" data-key="${item.key}">
                    <button type="button" class="qty-btn" data-action="qty-change" data-delta="1">+</button>
                  </div>
                  <div class="cart-drawer-item__price">${formatMoney(item.final_line_price)}</div>
                </div>
              </div>
            </div>
          `;
        });
        itemsHtml += '</div>';
        this.itemsContainer.innerHTML = itemsHtml;
      }
    }
  };

  // --- QUICK VIEW MODAL ENGINE ---
  const initQuickView = () => {
    const modal = document.getElementById('quick-view-modal');
    const content = document.getElementById('quick-view-content');
    if (!modal || !content) return;

    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-action="quick-view"]');
      if (btn) {
        e.preventDefault();
        const handle = btn.getAttribute('data-handle');
        if (!handle) return;

        modal.classList.add('active');
        document.body.style.overflow = 'hidden';
        content.innerHTML = `
          <div class="quick-view-loading text-center" style="padding: 40px 0;">
            <div class="luxury-spinner"></div>
            <p style="color: var(--color-accent-gold); font-size: 0.85rem; letter-spacing: 0.1em;">INSPECTING TIMEPIECE ARCHIVE...</p>
          </div>
        `;

        fetch(`/products/${handle}.js`)
          .then((res) => res.json())
          .then((product) => {
            renderQuickViewProduct(product);
          })
          .catch(() => {
            content.innerHTML = '<p class="text-center" style="padding: 20px;">Unable to load timepiece details.</p>';
          });
      }
    });

    const renderQuickViewProduct = (product) => {
      const firstVariant = product.variants[0];
      const hasVariants = product.variants.length > 1;

      content.innerHTML = `
        <div class="quick-view-product-grid" style="display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 30px;">
          <div class="qv-media" style="background: var(--color-bg-secondary); border: 1px solid var(--color-border); border-radius: 4px; overflow: hidden;">
            <img id="qv-main-img" src="${product.featured_image}" alt="${product.title}" style="width: 100%; aspect-ratio: 1/1; object-fit: cover;">
          </div>
          <div class="qv-details">
            <span class="gold-subtitle">${product.vendor || 'MIYAAR HOROLOGY'}</span>
            <h2 style="font-size: 1.5rem; margin-bottom: 10px; color: var(--color-text-primary);">${product.title}</h2>
            <div style="font-size: 1.3rem; color: var(--color-accent-gold); font-weight: 700; margin-bottom: 16px;">
              ${formatMoney(firstVariant.price)}
            </div>
            <div style="font-size: 0.85rem; color: var(--color-text-secondary); line-height: 1.6; margin-bottom: 20px;">
              ${product.description ? product.description.replace(/<[^>]*>?/gm, '').substring(0, 180) + '...' : ''}
            </div>

            <div style="margin-bottom: 24px;">
              <button type="button" class="btn btn--primary btn--full" data-action="quick-add" data-variant-id="${firstVariant.id}">
                Acquire Timepiece &bull; ${formatMoney(firstVariant.price)}
              </button>
            </div>

            <div style="border-top: 1px solid var(--color-border); padding-top: 14px; text-align: center;">
              <a href="${product.url}" class="btn-link" style="font-size: 0.75rem;">Full Horological Dossier & Specifications &rarr;</a>
            </div>
          </div>
        </div>
      `;
    };
  };

  // --- SIZE GUIDE MODAL ---
  const initSizeGuideModal = () => {
    const modal = document.getElementById('size-guide-modal');
    if (!modal) return;

    document.querySelectorAll('[data-action="open-size-guide"]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        modal.classList.add('active');
        document.body.style.overflow = 'hidden';
      });
    });

    document.querySelectorAll('[data-action="close-modal"]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const activeModal = btn.closest('.luxury-modal');
        if (activeModal) {
          activeModal.classList.remove('active');
          document.body.style.overflow = '';
        }
      });
    });
  };

  // --- INTERACTIVE WATCH ANATOMY HOTSPOTS ---
  const initInteractiveAnatomy = () => {
    const section = document.querySelector('.interactive-spec-section');
    if (!section) return;

    const pins = section.querySelectorAll('.hotspot-pin');
    const cards = section.querySelectorAll('.spec-card-item');

    const activateIndex = (index) => {
      pins.forEach((pin, i) => {
        pin.classList.toggle('active', i === index);
      });
      cards.forEach((card, i) => {
        card.classList.toggle('active', i === index);
      });
    };

    pins.forEach((pin) => {
      pin.addEventListener('click', () => {
        const idx = parseInt(pin.getAttribute('data-hotspot-index'), 10);
        activateIndex(idx);
      });
      pin.addEventListener('mouseenter', () => {
        const idx = parseInt(pin.getAttribute('data-hotspot-index'), 10);
        activateIndex(idx);
      });
    });

    cards.forEach((card) => {
      card.addEventListener('click', () => {
        const idx = parseInt(card.getAttribute('data-spec-card'), 10);
        activateIndex(idx);
      });
    });
  };

  // --- MAIN PRODUCT PAGE ENGINE (VARIANTS, ZOOM, STICKY ATC) ---
  const initProductPage = () => {
    const productSection = document.querySelector('.main-product-section');
    if (!productSection) return;

    const sectionId = productSection.getAttribute('data-section-id');
    const jsonScript = document.getElementById(`ProductJson-${sectionId}`);
    if (!jsonScript) return;

    let productData;
    try {
      productData = JSON.parse(jsonScript.textContent);
    } catch (e) {
      return;
    }

    const formatDeliveryDate = (date) => {
      const day = date.getDate();
      const remainder = day % 100;
      const ordinal = remainder >= 11 && remainder <= 13
        ? 'th'
        : ({ 1: 'st', 2: 'nd', 3: 'rd' }[day % 10] || 'th');
      const month = new Intl.DateTimeFormat('en', { month: 'short' }).format(date);
      return `${month} ${day}${ordinal}`;
    };
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    productSection.querySelectorAll('[data-delivery-date-start]').forEach((dateElement) => {
      const startOffset = Number(dateElement.getAttribute('data-delivery-date-start'));
      const startDate = new Date(today);
      startDate.setDate(today.getDate() + startOffset);
      let dateText = formatDeliveryDate(startDate);

      if (dateElement.hasAttribute('data-delivery-date-end')) {
        const endOffset = Number(dateElement.getAttribute('data-delivery-date-end'));
        const endDate = new Date(today);
        endDate.setDate(today.getDate() + endOffset);
        dateText += ` – ${formatDeliveryDate(endDate)}`;
      }

      dateElement.textContent = dateText;
    });

    const exitOffer = document.getElementById('ProductExitOffer');
    const exitOfferKey = `miyaar-exit-offer-${productSection.getAttribute('data-product-handle')}`;
    let exitOfferShown = sessionStorage.getItem(exitOfferKey) === 'shown';
    const showExitOffer = () => {
      if (exitOfferShown || !exitOffer) return;
      exitOfferShown = true;
      sessionStorage.setItem(exitOfferKey, 'shown');
      exitOffer.classList.add('active');
      exitOffer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    };
    const closeExitOffer = () => {
      if (!exitOffer) return;
      exitOffer.classList.remove('active');
      exitOffer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    };

    if (exitOffer && !exitOfferShown) {
      history.pushState({ miyaarProductExitOffer: true }, '', window.location.href);
      window.addEventListener('popstate', showExitOffer, { once: true });
      document.addEventListener('mouseout', (event) => {
        if (event.clientY <= 0 && event.relatedTarget === null) showExitOffer();
      });
      exitOffer.querySelectorAll('[data-action="close-exit-offer"]').forEach((button) => {
        button.addEventListener('click', closeExitOffer);
      });
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && exitOffer.classList.contains('active')) closeExitOffer();
      });
    }

    const form = document.getElementById('MainProductForm');
    const selectedVariantInput = document.getElementById('SelectedVariantId');
    const addToCartBtn = document.getElementById('MainAddToCartButton');
    const priceDisplay = document.getElementById(`ProductPrice-${sectionId}`);
    const stickyBar = document.getElementById('StickyAtcBar');
    const mainImg = document.getElementById(`MainProductImage-${sectionId}`);
    const quantityInput = document.getElementById('ProductQuantityInput');
    const bundleOptions = productSection.querySelectorAll('.product-bundle-option');
    let selectedVariant = productData.variants.find((variant) => String(variant.id) === selectedVariantInput?.value) || productData.variants[0];

    const updateBundleOptions = (unitPrice, unitCompareAtPrice) => {
      bundleOptions.forEach((option) => {
        const quantity = Number(option.getAttribute('data-bundle-quantity'));
        const total = option.querySelector('[data-bundle-total]');
        const original = option.querySelector('[data-bundle-original]');
        if (total) total.textContent = formatMoney(unitPrice * quantity);
        if (original) original.textContent = formatMoney(unitCompareAtPrice * quantity);
      });
    };

    const syncBundleSelection = (quantity) => {
      bundleOptions.forEach((option) => {
        const isSelected = Number(option.getAttribute('data-bundle-quantity')) === quantity;
        option.setAttribute('aria-pressed', String(isSelected));
      });
    };

    updateBundleOptions(selectedVariant.price, selectedVariant.compare_at_price || selectedVariant.price);
    syncBundleSelection(Number(quantityInput?.value || 1));
    bundleOptions.forEach((option) => {
      option.addEventListener('click', () => {
        const quantity = Number(option.getAttribute('data-bundle-quantity'));
        if (!quantityInput || !Number.isInteger(quantity)) return;
        quantityInput.value = String(quantity);
        syncBundleSelection(quantity);
      });
    });

    // Quantity selectors
    productSection.querySelectorAll('[data-action="decrease-product-qty"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (quantityInput && quantityInput.value > 1) {
          quantityInput.value = parseInt(quantityInput.value, 10) - 1;
          syncBundleSelection(Number(quantityInput.value));
        }
      });
    });
    productSection.querySelectorAll('[data-action="increase-product-qty"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (quantityInput && quantityInput.value < 99) {
          quantityInput.value = parseInt(quantityInput.value, 10) + 1;
          syncBundleSelection(Number(quantityInput.value));
        }
      });
    });
    quantityInput?.addEventListener('change', () => {
      const quantity = Math.min(99, Math.max(1, parseInt(quantityInput.value, 10) || 1));
      quantityInput.value = String(quantity);
      syncBundleSelection(quantity);
    });

    // Variant options radio change
    const optionRadios = form.querySelectorAll('.variant-pill-input');
    optionRadios.forEach((radio) => {
      radio.addEventListener('change', () => {
        // Collect current selections
        const selectedOptions = [];
        const groups = form.querySelectorAll('.variant-option-group');
        groups.forEach((group, groupIdx) => {
          const checked = group.querySelector('.variant-pill-input:checked');
          if (checked) {
            selectedOptions.push(checked.value);
            const displayLabel = document.getElementById(`OptionValueDisplay-${groupIdx}`);
            if (displayLabel) displayLabel.textContent = checked.value;
          }
        });

        // Find matching variant
        const matchingVariant = productData.variants.find((variant) => {
          return selectedOptions.every((val, idx) => variant.options[idx] === val);
        });

        if (matchingVariant) {
          selectedVariant = matchingVariant;
          if (selectedVariantInput) selectedVariantInput.value = matchingVariant.id;
          updateBundleOptions(matchingVariant.price, matchingVariant.compare_at_price || matchingVariant.price);

          // Update Price
          if (priceDisplay) {
            const price = Number(matchingVariant.price) || 319900;
            const compareAtPrice = Number(matchingVariant.compare_at_price) || 429900;
            const isOnSale = compareAtPrice > price;
            const discountPercent = isOnSale ? Math.round(((compareAtPrice - price) / compareAtPrice) * 100) : 0;
            const saleMarkup = isOnSale
              ? `
                <span class="price-item price-item--sale">${formatMoney(price)}</span>
                <s class="price-item price-item--regular price-item--compare">${formatMoney(compareAtPrice)}</s>
                <span class="luxury-badge badge-sale">-${discountPercent}% OFF</span>
              `
              : `<span class="price-item price-item--regular">${formatMoney(price)}</span>`;
            priceDisplay.innerHTML = `
              <div class="price-wrapper product-price--main ${isOnSale ? 'price--on-sale' : ''}">
                <div class="price-container">${saleMarkup}</div>
              </div>
            `;
          }

          // Update Add to Cart Button
          if (addToCartBtn) {
            if (matchingVariant.available) {
              addToCartBtn.disabled = false;
              addToCartBtn.classList.remove('btn--disabled');
              addToCartBtn.querySelector('.btn-text').textContent = 'Add to cart';
            } else {
              addToCartBtn.disabled = true;
              addToCartBtn.classList.add('btn--disabled');
              addToCartBtn.querySelector('.btn-text').textContent = window.MiyaarConfig.strings.soldOut;
            }
          }

          // Update image if variant has featured_image
          if (matchingVariant.featured_image && mainImg) {
            mainImg.src = matchingVariant.featured_image.src;
          }
        }
      });
    });

    // Form submission via AJAX to Cart Drawer
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const variantId = selectedVariantInput ? selectedVariantInput.value : productData.variants[0].id;
      const qty = quantityInput ? parseInt(quantityInput.value, 10) : 1;
      CartDrawer.addItem(variantId, qty, {}, addToCartBtn);
    });

    // Sticky Buy Bar scroll trigger
    if (stickyBar && form) {
      window.addEventListener('scroll', () => {
        const formRect = form.getBoundingClientRect();
        if (formRect.bottom < 0) {
          stickyBar.classList.add('active');
        } else {
          stickyBar.classList.remove('active');
        }
      }, { passive: true });

      const stickyBtn = stickyBar.querySelector('[data-action="sticky-add-to-cart"]');
      if (stickyBtn) {
        stickyBtn.addEventListener('click', () => {
          form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
        });
      }
    }
  };

  // --- COLLECTION FILTERING & SORTING ---
  const initCollection = () => {
    const sortSelect = document.getElementById('SortBySelect');
    if (sortSelect) {
      sortSelect.addEventListener('change', (e) => {
        const value = e.target.value;
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', value);
        window.location.href = url.href;
      });
    }

    const gridToggles = document.querySelectorAll('.grid-toggle-btn');
    const productsGrid = document.getElementById('CollectionProductsGrid');
    if (productsGrid && gridToggles.length > 0) {
      gridToggles.forEach((btn) => {
        btn.addEventListener('click', () => {
          gridToggles.forEach((b) => b.classList.remove('active'));
          btn.classList.add('active');
          const cols = btn.getAttribute('data-grid-cols');
          productsGrid.className = `products-grid products-grid--${cols}-col`;
        });
      });
    }
  };

  // --- CUSTOMER RECOVER PASSWORD TOGGLE ---
  const initCustomerAuth = () => {
    const recoverBtn = document.getElementById('RecoverPasswordBtn');
    const cancelRecoverBtn = document.getElementById('CancelRecoverBtn');
    const recoverBox = document.getElementById('RecoverPasswordForm');
    const loginForm = document.querySelector('.auth-form');

    if (recoverBtn && recoverBox) {
      recoverBtn.addEventListener('click', (e) => {
        e.preventDefault();
        recoverBox.style.display = 'block';
        if (loginForm) loginForm.style.display = 'none';
      });
    }

    if (cancelRecoverBtn && recoverBox) {
      cancelRecoverBtn.addEventListener('click', (e) => {
        e.preventDefault();
        recoverBox.style.display = 'none';
        if (loginForm) loginForm.style.display = 'block';
      });
    }
  };

  // --- MIYAAR WATCHES: COLOR / STRAP SWITCHER ON PRODUCT PAGE ---
  const initColorSwitcher = () => {
    const productSection = document.querySelector('.main-product-section');
    if (!productSection) return;

    const sectionId = productSection.getAttribute('data-section-id');
    const mainImg = document.getElementById(`MainProductImage-${sectionId}`) || document.querySelector('.product-gallery__main-image');
    const stickyThumb = document.getElementById('StickyAtcThumb');
    const productHandle = (productSection.getAttribute('data-product-handle') || window.location.pathname).toLowerCase();

    // Determine fallback asset URL if data-image-target is not on DOM
    const getFallbackImageForColor = (colorName) => {
      const c = (colorName || '').toLowerCase();
      if (productHandle.includes('roman')) {
        if (c.includes('mustard') || c.includes('yellow') || c.includes('tan')) {
          return '/assets/cartier-tank-roman-silver-mustard.jpg';
        }
        return '/assets/cartier-tank-roman-silver-black.jpg';
      } else if (productHandle.includes('silver')) {
        if (c.includes('brown') || c.includes('cognac')) {
          return '/assets/cartier-tank-minimalist-silver-brown.jpg';
        }
        return '/assets/cartier-tank-minimalist-silver-black.jpg';
      } else {
        if (c.includes('black') || c.includes('noir')) {
          return '/assets/cartier-tank-minimalist-gold-black.jpg';
        }
        return '/assets/cartier-tank-minimalist-gold-brown.jpg';
      }
    };

    const updateProductStrapImage = (targetImage, colorTitle, parentLabel, parentGroup) => {
      if (parentGroup) {
        parentGroup.querySelectorAll('.variant-pill-label--color').forEach((l) => l.classList.remove('active'));
        if (parentLabel) parentLabel.classList.add('active');
        const displayEl = parentGroup.querySelector('.variant-option-current-value') || document.getElementById('OptionValueDisplay');
        if (displayEl && colorTitle) displayEl.textContent = colorTitle;
      }

      const finalImg = targetImage || getFallbackImageForColor(colorTitle);
      if (mainImg && finalImg) {
        mainImg.style.transition = 'opacity 0.15s ease, transform 0.15s ease';
        mainImg.style.opacity = '0.35';
        mainImg.style.transform = 'scale(0.98)';
        setTimeout(() => {
          mainImg.src = finalImg;
          if (colorTitle) mainImg.alt = colorTitle;
          mainImg.style.opacity = '1';
          mainImg.style.transform = '';
        }, 120);
      }
      if (stickyThumb && finalImg) stickyThumb.src = finalImg;

      productSection.querySelectorAll('.product-gallery__color-swatch').forEach((swatch) => {
        const isActive = swatch.getAttribute('data-color-title')?.toLowerCase() === (colorTitle || '').toLowerCase();
        swatch.classList.toggle('active', isActive);
        swatch.setAttribute('aria-pressed', String(isActive));
      });

    };

    // Bind click on all color labels directly
    const colorLabels = productSection.querySelectorAll('.variant-pill-label--color');
    colorLabels.forEach((label) => {
      label.addEventListener('click', function(e) {
        const input = label.querySelector('input');
        if (input && !input.checked) {
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
        const targetImage = label.getAttribute('data-image-target');
        const colorTitle = label.getAttribute('data-color-title') || (input ? input.value : '');
        const parentGroup = label.closest('.variant-option-group');
        updateProductStrapImage(targetImage, colorTitle, label, parentGroup);
      });
    });

    // Also bind change on inputs
    const colorInputs = document.querySelectorAll('.color-switcher-input, .variant-pill-input');
    colorInputs.forEach((input) => {
      input.addEventListener('change', () => {
        const parentLabel = input.closest('.variant-pill-label--color');
        const parentGroup = input.closest('.variant-option-group');
        const targetImage = parentLabel ? parentLabel.getAttribute('data-image-target') : null;
        const colorTitle = (parentLabel && parentLabel.getAttribute('data-color-title')) || input.value;
        updateProductStrapImage(targetImage, colorTitle, parentLabel, parentGroup);
      });
    });

    productSection.querySelectorAll('.product-gallery__color-swatch').forEach((swatch) => {
      swatch.addEventListener('click', () => {
        const colorTitle = swatch.getAttribute('data-color-title') || '';
        const matchingLabel = Array.from(productSection.querySelectorAll('.variant-pill-label--color')).find((label) => {
          const labelTitle = label.getAttribute('data-color-title') || label.querySelector('input')?.value || '';
          return labelTitle.toLowerCase() === colorTitle.toLowerCase();
        });
        const input = matchingLabel?.querySelector('input');

        if (input) {
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        } else {
          const targetImage = getFallbackImageForColor(colorTitle);
          updateProductStrapImage(targetImage, colorTitle, null, null);
        }
      });
    });

    const colorSwatches = Array.from(productSection.querySelectorAll('.product-gallery__color-swatch'));
    const blackSwatch = colorSwatches.find((swatch) => {
      const title = (swatch.getAttribute('data-color-title') || '').toLowerCase();
      return title.includes('black') || title.includes('noir');
    });
    if (blackSwatch) {
      blackSwatch.parentElement.insertBefore(blackSwatch, blackSwatch.parentElement.firstElementChild);
      blackSwatch.click();
    }

  };

  // --- CARD SWATCH HOVER/CLICK IMAGE SWITCHER (Home Featured Collection & Catalog) ---
  const initCardSwatches = () => {
    document.querySelectorAll('.product-card').forEach((card) => {
      const swatches = card.querySelectorAll('.card-swatch-dot[data-card-image]');
      const primaryImg = card.querySelector('.product-card__image--primary');
      const secondaryImg = card.querySelector('.product-card__image--secondary');
      if (!swatches.length || !primaryImg) return;

      // Mark card so CSS secondary hover doesn't fight swatches
      card.classList.add('has-swatches');

      const applyImage = (src) => {
        if (!src || primaryImg.src.includes(src)) return;
        primaryImg.style.opacity = '0.6';
        setTimeout(() => {
          primaryImg.src = src;
          primaryImg.style.opacity = '1';
        }, 80);
        if (secondaryImg) {
          secondaryImg.style.opacity = '0';
        }
      };

      swatches.forEach((swatch) => {
        // Hover: preview strap variant
        swatch.addEventListener('mouseenter', (e) => {
          e.stopPropagation();
          const img = swatch.getAttribute('data-card-image');
          applyImage(img);
        });

        // Click: lock in strap variant
        swatch.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          swatches.forEach((s) => {
            s.classList.remove('active');
            s.setAttribute('aria-pressed', 'false');
          });
          swatch.classList.add('active');
          swatch.setAttribute('aria-pressed', 'true');
          const img = swatch.getAttribute('data-card-image');
          applyImage(img);
        });

        // Mouse leave: restore active swatch image
        swatch.addEventListener('mouseleave', () => {
          const activeSwatch = card.querySelector('.card-swatch-dot.active[data-card-image]');
          if (activeSwatch) {
            const img = activeSwatch.getAttribute('data-card-image');
            applyImage(img);
          }
        });
      });
    });
  };

  // --- INITIALIZE ALL MODULES ---
  document.addEventListener('DOMContentLoaded', () => {
    initHeader();
    initMobileMenu();
    initSearchModal();
    CartDrawer.init();
    initQuickView();
    initSizeGuideModal();
    initInteractiveAnatomy();
    initProductPage();
    initColorSwitcher();
    initCardSwatches();
    initCollection();
    initCustomerAuth();
  });
})();
